/**
 * Signup-funnel data for the admin dashboard.
 *
 * Refetches whenever the window or filters change, so the tab has no manual
 * "Load" step. Gated on `enabled` because the page only has usable credentials
 * after the admin basic-auth modal is submitted.
 */

import { useQuery } from '@tanstack/react-query'
import { getAdminSignupFunnel } from '@/lib/api/api'
import type { SignupFunnelResponse } from '@/lib/api/admin-types'
import { resolveAdminFunnelWindow, type AdminFunnelWindow } from '@/lib/utils/adminPeriod'

type UseAdminSignupFunnelArgs = {
  token: string | null
  /** App-wide DateDropdown period, e.g. "mtd" or "2026-08-01:2026-08-17". */
  period: string
  includeTestAccounts: boolean
  device?: string
  enabled: boolean
}

type UseAdminSignupFunnelReturn = {
  data: SignupFunnelResponse | undefined
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  /** Resolved window the request was built from. */
  range: AdminFunnelWindow
  refetch: () => void
}

export function useAdminSignupFunnel({
  token,
  period,
  includeTestAccounts,
  device,
  enabled,
}: UseAdminSignupFunnelArgs): UseAdminSignupFunnelReturn {
  const range = resolveAdminFunnelWindow(period)

  const query = useQuery<SignupFunnelResponse>({
    queryKey: ['admin-signup-funnel', period, includeTestAccounts, device ?? ''],
    queryFn: () =>
      getAdminSignupFunnel(token as string, {
        preset: range.preset,
        startDate: range.startDate,
        endDate: range.endDate,
        test: includeTestAccounts,
        device: device || undefined,
      }),
    enabled: enabled && !!token,
    staleTime: 60_000,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    range,
    refetch: query.refetch,
  }
}
